/** Est. Money Needed — v1 permits-roi.js estMoneyNeeded/estMoneyAfter +
 * leadRehabValue + transactions.js txnCard chip rules, verbatim.
 *
 * FOR CLOSING (Tom, 2026-07-05): the check you bring to the closing table —
 * down payment (purchase − loan, 0 at 100% LTV) + origination points on
 * (loan + rehab) + loan service fee + buying costs. EMD already paid comes
 * OFF this number (floor 0).
 * AFTER closing: carrying the deal — monthly interest × utility months +
 * utilities × months + staging.
 * Chip stages (Tom, 2026-07-05): Contract Signed shows both; Closed shows
 * only "After" (closing money has been paid); later stages show neither. */
import { flipResult, roiTier, type NrInputs } from "./math";

const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** The stored per-property ROI override, if any. v1 keys NR_BY by the RAW
 * address, so lookup tolerates case/space drift — one place, so the reader and
 * the Save button in RoiCalculator can never disagree about which entry they
 * are looking at. */
export function nrOverrideFor(nrBy: unknown, address: string): Record<string, number> | undefined {
  if (nrBy === null || typeof nrBy !== "object" || Array.isArray(nrBy)) return undefined;
  const map = nrBy as Record<string, unknown>;
  let entry = map[address];
  if (entry === undefined) {
    const want = String(address).toLowerCase().trim();
    for (const k of Object.keys(map)) {
      if (k.toLowerCase().trim() === want) { entry = map[k]; break; }
    }
  }
  if (entry === null || typeof entry !== "object" || Array.isArray(entry)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(entry as Record<string, unknown>)) {
    const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

/** v1 leadRehabValue minus the p.rehab head (scraped rows carry no rehab):
 * NR_BY[address].rehab → edits.roiCalc.repairs → edits.rehabCalc.total. */
export function dealRehabValue(
  address: string,
  nrBy: unknown,
  leadFields: Record<string, unknown> | undefined,
): number {
  {
    const entry = nrOverrideFor(nrBy, address);
    if (entry) {
      const r = num(entry["rehab"]);
      if (r > 0) return r;
    }
  }
  const roiCalc = leadFields?.["roiCalc"];
  if (roiCalc !== null && typeof roiCalc === "object" && !Array.isArray(roiCalc)) {
    const r = num((roiCalc as Record<string, unknown>)["repairs"]);
    if (r > 0) return r;
  }
  const rehabCalc = leadFields?.["rehabCalc"];
  if (rehabCalc !== null && typeof rehabCalc === "object" && !Array.isArray(rehabCalc)) {
    const r = num((rehabCalc as Record<string, unknown>)["total"]);
    if (r > 0) return r;
  }
  return 0;
}

/** v1 estMoneyNeeded(purchase, rehab, ltvPct) with NR_MASTER `m`. */
export function estMoneyNeeded(purchase: number, rehab: number, ltvPct: number | null, m: NrInputs): number {
  const ltv = ltvPct !== null && ltvPct > 0 ? ltvPct : m.ltv;
  const loan = (purchase * ltv) / 100;
  const down = Math.max(purchase - loan, 0);
  const orig = ((loan + rehab) * m.pts) / 100;
  const svc = m.svc;
  const buying = (purchase * (m.kiwi + m.buy)) / 100;
  return Math.round(down + orig + svc + buying);
}

/** v1 estMoneyAfter(purchase, rehab, ltvPct): utilm = MONTHS, utilmo = $/mo. */
export function estMoneyAfter(purchase: number, rehab: number, ltvPct: number | null, m: NrInputs): number {
  void rehab; // v1 signature keeps it; the formula doesn't use it
  const ltv = ltvPct !== null && ltvPct > 0 ? ltvPct : m.ltv;
  const loan = (purchase * ltv) / 100;
  const months = m.utilm;
  const interest = ((loan * (m.rate / 100)) / 12) * months;
  const utilities = m.utilmo * months;
  return Math.round(interest + utilities + m.staging);
}

export interface DealKpis {
  purchase: number;
  rehab: number;
  arv: number | null; // from the linked lead's ARV edits (comped > listed > arv)
  profit: number | null; // flipResult with the live NR master (null without ARV)
  roi: number | null; // % — v1's kpi-roi element on the TXN detail
  tier: "good" | "ok" | "bad" | null;
}

/** v1 TXN-detail KPI strip: Purchase · Rehab · projected Profit/ROI through
 * the SAME flipResult the Net ROI calculator runs, seeded from the deal's
 * purchase, the rehab chain, and the linked lead's ARV edits. */
export function dealKpis(
  fields: Record<string, unknown>,
  rehab: number,
  leadArv: number | null,
  m: NrInputs,
): DealKpis {
  const purchase = num(fields["t_purchase"]) || num(fields["purchase"]);
  const arv = leadArv !== null && leadArv > 0 ? leadArv : null;
  if (!purchase || arv === null) {
    return { purchase, rehab, arv, profit: null, roi: null, tier: null };
  }
  const r = flipResult({ ...m, arv, purchase, rehab });
  return { purchase, rehab, arv, profit: Math.round(r.profit), roi: r.roi, tier: roiTier(r.roi) };
}

export interface MoneyChips {
  closing: { amount: number; pct: number; approved: boolean } | null;
  after: { amount: number } | null;
}

/** v1 txnCard chip block: stage gate + EMD subtraction + lender-LTV rule. */
export function dealMoneyChips(
  stageIdx: number,
  fields: Record<string, unknown>,
  rehab: number,
  m: NrInputs,
): MoneyChips {
  const showClosing = stageIdx === 0; // Contract Signed / Pending
  // Est. Money After Closing rides the card until SOLD (Tom 2026-08-11:
  // "it should be there until its in the sold stage" — it used to vanish
  // the moment a deal moved from Closed into Rehab). Stage 6 = Sold.
  const showAfter = stageIdx < 6;
  if (!showClosing && !showAfter) return { closing: null, after: null };
  const pur = num(fields["t_purchase"]) || num(fields["purchase"]);
  if (!pur) return { closing: null, after: null };
  const emd = num(fields["t_emd"]);
  const aLtvRaw = parseFloat(String(fields["t_lenderLtv"] ?? "").replace(/[^0-9.]/g, ""));
  const aLtv = Number.isFinite(aLtvRaw) && aLtvRaw > 0 ? aLtvRaw : null;
  const pct = aLtv !== null ? aLtv : m.ltv;
  const est = Math.max(0, estMoneyNeeded(pur, rehab, aLtv, m) - emd);
  const aft = estMoneyAfter(pur, rehab, aLtv, m);
  return {
    closing: showClosing ? { amount: est, pct, approved: aLtv !== null } : null,
    after: showAfter && aft ? { amount: aft } : null,
  };
}

export interface LeadRoi { purchase: number; arv: number | null; rehab: number; profit: number | null; roi: number | null; tier: "good" | "ok" | "bad" | null; }

/** Lead-drawer Est-ROI tile (v1 lead KPI "Est. ROI" = p.roi): the SAME flipResult
 * the Net ROI calculator runs, seeded from the lead's offer/asking price, its ARV
 * edits, and the rehab chain. Null profit/ROI until both a price and ARV exist. */
export function leadRoi(purchase: number, arv: number | null, rehab: number, m: NrInputs): LeadRoi {
  if (!purchase || arv === null || arv <= 0) return { purchase, arv, rehab, profit: null, roi: null, tier: null };
  const r = flipResult({ ...m, arv, purchase, rehab });
  return { purchase, arv, rehab, profit: Math.round(r.profit), roi: r.roi, tier: roiTier(r.roi) };
}
